import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { Avatar, Dropdown, DropdownItem, DropdownSeparator, Icon, type IconName } from "@/components/ui";
import { useLanguage } from "@/lib/i18n/language";
import { signOut } from "@/lib/auth/client";
import { cn } from "@/lib/utils";

/** A sidebar nav entry. `label`/`group` are i18n keys (namespace `common`) resolved at render,
 *  so the topbar toggle re-translates the nav live (TW-3434). */
export interface NavItemDef {
  to: string;
  icon: IconName;
  /** Translation key for the item label, e.g. `nav.dashboard`. */
  label: string;
  /** Optional unread/queue count badge. */
  count?: number;
  /** Exact-match the route (use for the index "/" entry so it isn't always active). */
  end?: boolean;
}

export interface NavGroupDef {
  /** Translation key for the section heading (omitted for the primary group). */
  label?: string;
  items: NavItemDef[];
}

/** Renders the grouped nav with react-router active states (replaces the prototype's
 *  client-side `route === id` check). */
export function SidebarNav({ groups }: { groups: NavGroupDef[] }) {
  const { t } = useTranslation();
  return (
    <>
      {groups.map((group, i) => (
        <div className="nav-group" key={i}>
          {group.label && <div className="nav-label">{t(group.label)}</div>}
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => cn("nav-item", isActive && "active")}
            >
              <Icon name={item.icon} size={18} />
              <span>{t(item.label)}</span>
              {item.count ? <span className="nav-count">{item.count}</span> : null}
            </NavLink>
          ))}
        </div>
      ))}
    </>
  );
}

/** Topbar EN/ES toggle (persisted via <LanguageProvider>). */
export function LangToggle() {
  const { t } = useTranslation();
  const { lang, setLang } = useLanguage();
  return (
    <div className="lang-toggle" role="group" aria-label={t("shell.language")}>
      <button type="button" className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>
        EN
      </button>
      <button type="button" className={lang === "es" ? "active" : ""} onClick={() => setLang("es")}>
        ES
      </button>
    </div>
  );
}

/** Derive avatar initials from a display name (first letters of the first two words). */
export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

/** Topbar avatar → account menu (sign out). Replaces the prototype's bare avatar. */
export function UserMenu({ name, role, image }: { name: string; role: string; image?: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <Dropdown trigger={<Avatar initials={initials(name)} src={image} size={36} />}>
      <div style={{ padding: "8px 10px 6px", minWidth: 0 }}>
        <div style={{ fontWeight: 650, fontSize: 13.5 }}>{name}</div>
        <div className="faint" style={{ fontSize: 11.5 }}>
          {role}
        </div>
      </div>
      <DropdownSeparator />
      <DropdownItem label={t("nav.settings")} icon="settings" onSelect={() => navigate("/settings")} />
      <DropdownItem label={t("nav.signOut")} icon="logout" onSelect={handleSignOut} />
    </Dropdown>
  );
}
